import { defineStore } from 'pinia';
import AuthService from 'src/services/AuthService';
import { UserInfo } from '../types';
import { useLocalStorage } from '@vueuse/core';

// Default state function
const getDefaultState = () => ({
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  type: '',
  avatar: '',
  is_email_verified: false,
  onboardingDone: undefined as Date | null | undefined,
  lastAcknowledgedLevel: 1,
  petName: '',
  stripe_product_id: '',
  stripe_subscription_is_active: true,
  stripe_subscription_plan: '',
  has_financial_features: false,
  isAdmin: false,
  agencyId: '',
  agencyName: '',
  agencyLogo: '',
  studentLevel: 1,
  accountType: '',
  petLevel: 0,
  language: '',
  isZoomLinked: false,
  meeting_platform_type: '',
  zoomLicenseType: '',
  zoomEmail: '',
  isEnrolledToZoomCourse: false,
  displayName: '',
  country: '',
  isIndependentTeacherAdmin: false,
  isAcademyTeacher: false,
  paymentsFeatureSeen: true,
});

export const useUserInfoStore = defineStore('user-info', {
  state: () => {
    const storedData = JSON.parse(localStorage.getItem('user-info')) || {};

    const mergedState = { ...getDefaultState(), ...storedData };

    localStorage.setItem('user-info', JSON.stringify(mergedState));

    return useLocalStorage('user-info', mergedState);
  },
  getters: {
    fullName: (state) => `${state.firstName} ${state.lastName}`,
    // isPaymentEnabled: (state) => true, // Always true for now
  },
  actions: {
    async fetchUserInfo() {
      const [error, data] = await AuthService.getUserInfo();
      if (error) {
        console.error(error);
        return;
      }
      if (data) {
        this.id = data.id;
        this.email = data.email;
        this.firstName = data.first_name;
        this.lastName = data.last_name;
        this.type = data.type;
        this.avatar = data.avatar;
        this.is_email_verified = data.is_email_verified;
        this.onboardingDone = data.user_metadata.onboarding_done as Date | null;
        this.lastAcknowledgedLevel = data.user_metadata.last_acknowledged_level;
        this.stripe_product_id = data.stripe_product_id;
        this.isAdmin = data.is_admin ?? false;
        if (!!data.agency_id) {
          this.agencyId = data.agency_id ?? '';
        }
        this.agencyName = data.agency_name ?? '';
        this.agencyLogo = data.agency_logo ?? '';
        this.stripe_subscription_is_active = data.stripe_subscription_is_active;
        this.stripe_subscription_plan = data.stripe_subscription_plan ?? '';
        this.has_financial_features = data.has_financial_features ?? false;
        this.accountType = data.account_type;
        this.petLevel = data.pet_level ?? 0;
        this.language = data.user_metadata.language ?? '';
        if (this.language) {
          localStorage.setItem('user-lang', this.language);
        }
        this.isZoomLinked = data.is_zoom_linked ?? false;
        this.meeting_platform_type = data.meeting_platform_type ?? 'new';
        this.zoomLicenseType = data.zoom_license_type ?? '';
        this.zoomEmail = data.zoom_email ?? '';
        this.displayName =
          data.user_metadata.meeting_display_name ||
          `${data.first_name} ${data.last_name}`;
          this.country = data.country ?? '';
        this.isIndependentTeacherAdmin = data.is_independent_teacher_admin ?? false;
        this.isAcademyTeacher = data.is_agency_teacher ?? false;
        this.paymentsFeatureSeen = data.user_metadata?.payments_feature_seen ?? false;
      }
      // Force re-sync with local storage
      const currentState = JSON.parse(
        localStorage.getItem('user-info') || '{}'
      );
      localStorage.setItem(
        'user-info',
        JSON.stringify({ ...currentState, ...this.$state })
      );
    },
    setUserInfo(userData: UserInfo) {
      this.id = userData.id;
      this.email = userData.email;
      this.firstName = userData.first_name;
      this.lastName = userData.last_name;
      this.type = userData.type;
      this.avatar = userData.avatar;
      this.is_email_verified = userData.is_email_verified;
      this.onboardingDone = userData.user_metadata
        ?.onboarding_done as Date | null;
      this.lastAcknowledgedLevel =
        userData.user_metadata?.last_acknowledged_level || 1;
      this.stripe_product_id = userData.stripe_product_id;
      this.stripe_subscription_is_active =
        userData.stripe_subscription_is_active;
      this.stripe_subscription_plan = userData.stripe_subscription_plan || '';
      this.has_financial_features = userData.has_financial_features ?? false;
      this.isAdmin = userData.is_admin;
      this.agencyId = userData.agency_id;
      this.agencyName = userData.agency_name;
      this.agencyLogo = userData.agency_logo;
      this.accountType = userData.account_type;
      this.petLevel = userData.pet_level ?? 0;
      this.language = userData.user_metadata?.language ?? '';
      if (this.language) {
        localStorage.setItem('user-lang', this.language);
      }
      this.isZoomLinked = userData.is_zoom_linked ?? false;
      this.meeting_platform_type = userData.meeting_platform_type ?? 'new';
      this.zoomLicenseType = userData.zoom_license_type ?? '';
      this.zoomEmail = userData.zoom_email ?? '';
      this.displayName =
        userData.user_metadata?.meeting_display_name ||
        `${userData.first_name} ${userData.last_name}`;
      this.country = userData.country ?? '';      this.isIndependentTeacherAdmin = userData.is_independent_teacher_admin ?? false;
      this.isAcademyTeacher = userData.is_agency_teacher ?? false;
      this.paymentsFeatureSeen = userData.user_metadata?.payments_feature_seen ?? false;

    },
    clearUserInfo() {
      this.id = '';
      this.email = '';
      this.firstName = '';
      this.lastName = '';
      this.type = '';
      this.avatar = '';
      this.is_email_verified = false;
      this.stripe_product_id = '';
      this.stripe_subscription_is_active = true;
      this.stripe_subscription_plan = '';
      this.has_financial_features = false;
      this.isAdmin = false;
      this.agencyId = '';
      this.agencyName = '';
      this.agencyLogo = '';
      this.studentLevel = 1;
      this.accountType = '';
      this.isZoomLinked = false;
      this.zoomEmail = '';
      this.country = '';
    },
    setIsEmailVerified(isEmailVerified: boolean) {
      this.is_email_verified = isEmailVerified;
    },
    setPetName(petName: string) {
      this.petName = petName;
    },
    setAvatar(avatar: string) {
      this.avatar = avatar;
    },
    setStripeProductId(stripeProductId: string) {
      this.stripe_product_id = stripeProductId;
    },
    setStripeSubscriptionIsActive(stripeSubscriptionIsActive: boolean) {
      this.stripe_subscription_is_active = stripeSubscriptionIsActive;
    },
    setStripeSubscriptionPlan(stripeSubscriptionPlan: string) {
      this.stripe_subscription_plan = stripeSubscriptionPlan;
    },
    setHasFinancialFeatures(hasFinancialFeatures: boolean) {
      this.has_financial_features = hasFinancialFeatures;
    },
    async fetchStripeProductId() {
      const [error, data] = await AuthService.getUserInfo();
      if (error) {
        console.error(error);
        return;
      }
      if (data) {
        this.stripe_product_id = data.stripe_product_id;
        this.stripe_subscription_is_active = data.stripe_subscription_is_active;
        this.stripe_subscription_plan = data.stripe_subscription_plan || '';
        this.has_financial_features = data.has_financial_features ?? false;
      }
    },
    setAgencyInfo(agencyId: string, agencyName: string) {
      this.agencyId = agencyId;
      this.agencyName = agencyName;
    },
    setAgencyId(agencyId: string) {
      this.agencyId = agencyId;
    },
    setAgencyName(agencyName: string) {
      this.agencyName = agencyName;
    },
    setIsAdmin(isAdmin: boolean) {
      this.isAdmin = isAdmin;
    },
    setEmail(email: string) {
      this.email = email;
    },
    setFirstName(firstName: string) {
      this.firstName = firstName;
    },
    setLastName(lastName: string) {
      this.lastName = lastName;
    },
    setStudentLevel(level: number) {
      this.studentLevel = level;
    },
    setPetLevel(level: number) {
      this.petLevel = level;
    },
    setLanguage(language: string) {
      this.language = language;
      localStorage.setItem('user-lang', language);
    },
    setEmailVerified(is_email_verified: boolean) {
      this.is_email_verified = true;
    },
    setIsEnrolledToZoomCourse(isEnrolled: boolean) {
      this.isEnrolledToZoomCourse = isEnrolled;
    },
  },
  persist: true,
});
